// Voice helpers for Stew chat.
//
// Recording path: MediaRecorder captures a short audio clip, then a backend
// edge function (stew-transcribe) transcribes it via the Lovable AI gateway.
// Audio is never stored on the client or server: captured, transcribed,
// discarded. The prior browser SpeechRecognition path was removed because
// it fails on browsers that block Google's speech service (e.g. Brave).
//
// Playback path: browser SpeechSynthesis for Stew's spoken replies.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const RECORD_MAX_SECONDS = 60;
export const RECORD_WARN_AT = 45; // start countdown after this many seconds

export function isSpeechSynthesisSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

function isMediaRecorderSupported(): boolean {
  return typeof window !== "undefined" && typeof (window as any).MediaRecorder !== "undefined";
}

function pickMimeType(): string {
  if (typeof (window as any).MediaRecorder === "undefined") return "audio/webm";
  const MR: any = (window as any).MediaRecorder;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    try {
      if (MR.isTypeSupported && MR.isTypeSupported(t)) return t;
    } catch {
      // ignore
    }
  }
  return "audio/webm";
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export type VoiceErrorHandler = (message: string) => void;

export interface VoiceRecorderState {
  supported: boolean;
  recording: boolean;
  transcribing: boolean;
  /** Seconds elapsed while recording. */
  elapsed: number;
  /** Seconds remaining, only meaningful once <= RECORD_MAX_SECONDS - RECORD_WARN_AT. */
  remaining: number;
  /** Start recording. Safe to call again after stop. */
  start: () => void;
  /** Stop and transcribe the current clip. */
  stop: () => void;
  /** Cancel the current recording without transcribing. */
  cancel: () => void;
}

/**
 * Voice-to-text via MediaRecorder + edge function. `onTranscript` receives the
 * final transcript string once transcription completes; the chat surface is
 * expected to append it to the composer with a space between existing text.
 */
export function useVoiceRecorder(
  onTranscript: (text: string) => void,
  onError?: VoiceErrorHandler,
): VoiceRecorderState {
  const [supported] = useState<boolean>(() => isMediaRecorderSupported());
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<string>("audio/webm");
  const cancelledRef = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(0);

  const cleanupStream = useCallback(() => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      // ignore
    }
    streamRef.current = null;
  }, []);

  const clearTimers = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    cleanupStream();
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setElapsed(0);
  }, [cleanupStream, clearTimers]);

  const transcribe = useCallback(
    async (blob: Blob) => {
      if (blob.size < 800) {
        onError?.("That recording was too short. Try again.");
        return;
      }
      setTranscribing(true);
      try {
        const audio_base64 = await blobToBase64(blob);
        const { data, error } = await supabase.functions.invoke(
          "stew-transcribe",
          { body: { audio_base64, mime: blob.type || mimeRef.current } },
        );
        if (error) {
          console.error("stew-transcribe invoke failed:", error);
          onError?.("We couldn't transcribe that. Please try again.");
          return;
        }
        const text = String((data as any)?.transcript || "").trim();
        if (!text) {
          onError?.("We didn't catch that. Try again a little closer to the mic.");
          return;
        }
        onTranscript(text);
      } catch (e) {
        console.error("stew-transcribe threw:", e);
        onError?.("We couldn't transcribe that. Please try again.");
      } finally {
        setTranscribing(false);
      }
    },
    [onError, onTranscript],
  );

  const start = useCallback(async () => {
    if (!isMediaRecorderSupported()) {
      onError?.("Voice input isn't supported in this browser. You can type your question instead.");
      return;
    }
    if (recorderRef.current || recording) return;
    cancelledRef.current = false;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e: any) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        onError?.("Microphone access is blocked. Turn it on for this site in your browser settings, then try again.");
      } else if (name === "NotFoundError") {
        onError?.("No microphone was found. Check that one is connected and try again.");
      } else {
        onError?.("Voice input couldn't start. You can type your question instead.");
      }
      return;
    }
    const mime = pickMimeType();
    mimeRef.current = mime;
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { mimeType: mime });
    } catch {
      try {
        rec = new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((t) => t.stop());
        onError?.("Voice input couldn't start. You can type your question instead.");
        return;
      }
    }
    streamRef.current = stream;
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const wasCancelled = cancelledRef.current;
      const blob = new Blob(chunksRef.current, { type: mimeRef.current });
      reset();
      if (wasCancelled) return;
      void transcribe(blob);
    };
    rec.onerror = () => {
      onError?.("Voice input hit an error. Please try again.");
      cancelledRef.current = true;
      try { rec.stop(); } catch { /* ignore */ }
      reset();
    };

    try {
      rec.start();
    } catch {
      onError?.("Voice input couldn't start. Try again in a moment.");
      reset();
      return;
    }
    startedAtRef.current = Date.now();
    setRecording(true);
    setElapsed(0);
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    autoStopRef.current = setTimeout(() => {
      // Auto-stop at the cap; transcribe what we captured.
      try { recorderRef.current?.stop(); } catch { /* ignore */ }
    }, RECORD_MAX_SECONDS * 1000);
  }, [onError, recording, reset, transcribe]);

  const stop = useCallback(() => {
    if (!recorderRef.current) return;
    try {
      recorderRef.current.stop();
    } catch {
      reset();
    }
  }, [reset]);

  const cancel = useCallback(() => {
    if (!recorderRef.current) {
      reset();
      return;
    }
    cancelledRef.current = true;
    try { recorderRef.current.stop(); } catch { /* ignore */ }
  }, [reset]);

  useEffect(() => () => {
    cancelledRef.current = true;
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    reset();
  }, [reset]);

  const remaining = Math.max(0, RECORD_MAX_SECONDS - elapsed);

  return { supported, recording, transcribing, elapsed, remaining, start, stop, cancel };
}

// Strip markdown to plain text for spoken output.
export function markdownToSpeech(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_~>]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function speak(text: string): void {
  if (!isSpeechSynthesisSupported()) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 1;
  u.pitch = 1;
  window.speechSynthesis.speak(u);
}

export function stopSpeaking(): void {
  if (!isSpeechSynthesisSupported()) return;
  window.speechSynthesis.cancel();
}

export function isSpeaking(): boolean {
  if (!isSpeechSynthesisSupported()) return false;
  return window.speechSynthesis.speaking;
}
